import {
	BatchEvaluationResponse,
	BooleanEvaluationResponse,
	VariantEvaluationResponse,
} from "@flipt-io/flipt/dist/evaluation/models";
import { createFeatureFlags } from "@rageeb/frontend-lib/feature-flags";
import { useMemo } from "react";
import { getUser } from "@/auth";
import {
	FLIPT_FLAGS,
	FLIPT_NAMESPACE,
	FLIPT_URL,
	IS_SELF_HOSTED,
	SELF_HOSTED_DISABLED_FLAGS,
} from "@/constants/constants";
import { FliptFlagType } from "@/types";

type FlagType = "boolean" | "variant";

type GetFlagProps = {
	flagKey: keyof typeof FLIPT_FLAGS | string;
	flagType?: FlagType;
};

type GetFliptFlag = <T extends FlagType = "boolean">(props: {
	flagKey: keyof typeof FLIPT_FLAGS;
	flagType?: T;
}) => Promise<
	T extends "boolean" ? BooleanEvaluationResponse : VariantEvaluationResponse
>;

type GetFliptFlags = (
	props: GetFlagProps[],
) => Promise<BatchEvaluationResponse>;

const featureFlags = createFeatureFlags({
	url: FLIPT_URL,
	namespace: FLIPT_NAMESPACE,
	getContext: () => ({ tenant_id: getUser()?.fraud_tenant_id }),
});

const isDisabledSelfHosted = (flagKey: string) =>
	IS_SELF_HOSTED && SELF_HOSTED_DISABLED_FLAGS.has(flagKey);

const getFliptFlag: GetFliptFlag = <T extends FlagType>({
	flagKey = "",
	flagType = "boolean",
}: GetFlagProps) => featureFlags.client.evaluate<T>(flagKey, flagType);

const getFliptFlags: GetFliptFlags = (flags: GetFlagProps[]) =>
	featureFlags.client.evaluateBatch(flags.map(({ flagKey }) => ({ flagKey })));

const useGetFliptBooleanFlag = (flag: keyof typeof FLIPT_FLAGS) => {
	const result = featureFlags.useFeatureFlag(flag);

	return isDisabledSelfHosted(flag) ? { ...result, isEnabled: false } : result;
};

const useGetFliptBatchFlags = (flags: (keyof typeof FLIPT_FLAGS)[]) => {
	const { flags: flagList, isLoading } = featureFlags.useFeatureFlags(flags);

	const effectiveFlags = useMemo(
		() =>
			flagList &&
			Object.fromEntries(
				Object.entries(flagList).map(([key, isEnabled]) => [
					key,
					isEnabled && !isDisabledSelfHosted(key),
				]),
			),
		[flagList],
	);

	return {
		flagList: effectiveFlags as FliptFlagType | undefined,
		isLoading,
	};
};

export {
	getFliptFlag,
	getFliptFlags,
	useGetFliptBooleanFlag,
	useGetFliptBatchFlags,
};
